import React, { useEffect, useState } from 'react';
import {
  IconBan,
  IconChevronDown,
  IconChevronUp,
  IconCoin,
  IconPlus,
  IconTrash,
} from '@tabler/icons-react';
import {
  BalanceFilters,
  CustomBalanceFilter,
  RobinhoodFilters,
} from '../../api/robinhoodPresetsClient';

const CONTRACT_PATTERN = /^0x[a-fA-F0-9]{40}$/;
const MAX_UINT256 = '115792089237316195423570985008687907853269984665640564039457584007913129639935';
export const BUILT_IN_BALANCE_PRECISION = 5;

const normalizedInteger = (value: string) => value.replace(/^0+(?=\d)/, '');

const compareIntegerStrings = (left: string, right: string) => {
  const normalizedLeft = normalizedInteger(left);
  const normalizedRight = normalizedInteger(right);
  if (normalizedLeft.length !== normalizedRight.length) return normalizedLeft.length - normalizedRight.length;
  return normalizedLeft === normalizedRight ? 0 : normalizedLeft > normalizedRight ? 1 : -1;
};

export const compareRawAmounts = compareIntegerStrings;
export const isRawUInt256 = (value: string) => /^\d+$/.test(value) && compareIntegerStrings(value, MAX_UINT256) <= 0;

const DECIMAL_PATTERN = /^(?:\d+(?:\.\d*)?|\.\d+)$/;

export const isDecimalBalance = (value: string, maxDecimals: number) => (
  DECIMAL_PATTERN.test(value) && (value.split('.')[1] || '').length <= maxDecimals
);

const canonicalDecimal = (value: string) => {
  const [wholePart = '0', fractionPart = ''] = value.split('.');
  const whole = (wholePart || '0').replace(/^0+(?=\d)/, '') || '0';
  const fraction = fractionPart.replace(/0+$/, '');
  return fraction ? `${whole}.${fraction}` : whole;
};

export const decimalBalanceToApi = (value: string | null | undefined) => {
  if (value == null) return null;
  return canonicalDecimal(value);
};

export const apiBalanceToDecimal = (value: string | number | null | undefined) => (
  value == null ? null : canonicalDecimal(String(value))
);

export const compareDecimalBalances = (left: string, right: string) => {
  const [leftWhole, leftFraction = ''] = canonicalDecimal(left).split('.');
  const [rightWhole, rightFraction = ''] = canonicalDecimal(right).split('.');
  const wholeComparison = compareIntegerStrings(leftWhole, rightWhole);
  if (wholeComparison !== 0) return wholeComparison;
  const fractionLength = Math.max(leftFraction.length, rightFraction.length);
  const paddedLeft = leftFraction.padEnd(fractionLength, '0');
  const paddedRight = rightFraction.padEnd(fractionLength, '0');
  return paddedLeft === paddedRight ? 0 : paddedLeft > paddedRight ? 1 : -1;
};

function DecimalBalanceInput({
  label,
  token,
  value,
  maxDecimals,
  onChange,
}: {
  label: 'FROM' | 'TO';
  token: 'WETH' | 'ETH' | 'USDG' | 'Total ETH';
  value: string | null;
  maxDecimals: number;
  onChange: (value: string | null) => void;
}) {
  const [text, setText] = useState(value == null ? '' : String(value));
  const [error, setError] = useState<string | null>(null);

  useEffect(() => setText(value == null ? '' : String(value)), [value]);

  const commit = () => {
    const normalized = text.trim().replace(',', '.');
    if (!normalized) {
      setError(null);
      setText('');
      onChange(null);
      return;
    }
    if (!isDecimalBalance(normalized, maxDecimals)) {
      setError(`Use up to ${maxDecimals} decimal places`);
      return;
    }
    const canonical = canonicalDecimal(normalized);
    setError(null);
    setText(canonical);
    onChange(canonical);
  };

  return (
    <label className={error ? 'invalid' : ''}>
      <span>{label}</span>
      <input
        type="text"
        inputMode="decimal"
        aria-label={`${token} ${label === 'FROM' ? 'minimum' : 'maximum'}`}
        aria-invalid={Boolean(error)}
        placeholder={label === 'FROM' ? 'No minimum' : 'No maximum'}
        value={text}
        onChange={(event) => { setText(event.currentTarget.value); setError(null); }}
        onBlur={commit}
        onKeyDown={(event) => { if (event.key === 'Enter') event.currentTarget.blur(); }}
      />
      {error && <small className="rh-balance-input-error">{error}</small>}
    </label>
  );
}

function BuiltInBalanceRange({
  token,
  decimals,
  min,
  max,
  description,
  wide = false,
  children,
  onMinChange,
  onMaxChange,
}: {
  token: 'WETH' | 'ETH' | 'USDG' | 'Total ETH';
  decimals: number;
  min: string | null;
  max: string | null;
  description: string;
  wide?: boolean;
  children?: React.ReactNode;
  onMinChange: (value: string | null) => void;
  onMaxChange: (value: string | null) => void;
}) {
  const active = min != null || max != null;

  return (
    <div className={`rh-balance-token ${wide ? 'wide' : ''} ${active ? 'active' : ''}`}>
      <div className="rh-balance-token-head">
        <div>
          <strong>{token}</strong>
          <span>{description}</span>
        </div>
        {active && (
          <button type="button" title={`Clear ${token}`} onClick={() => { onMinChange(null); onMaxChange(null); }}>
            <IconBan size={14} />
          </button>
        )}
      </div>
      <div className="rh-balance-range-inputs">
        <DecimalBalanceInput label="FROM" token={token} value={min} maxDecimals={decimals} onChange={onMinChange} />
        <DecimalBalanceInput label="TO" token={token} value={max} maxDecimals={decimals} onChange={onMaxChange} />
      </div>
      <small>Up to {BUILT_IN_BALANCE_PRECISION} decimal places; point or comma.</small>
      {children}
    </div>
  );
}

function CustomBalanceRow({
  value,
  index,
  onChange,
  onRemove,
}: {
  value: CustomBalanceFilter;
  index: number;
  onChange: (value: CustomBalanceFilter) => void;
  onRemove: () => void;
}) {
  const minInvalid = value.min != null && !isRawUInt256(value.min);
  const maxInvalid = value.max != null && !isRawUInt256(value.max);
  const rangeInvalid = !minInvalid && !maxInvalid && value.min != null && value.max != null
    && compareIntegerStrings(value.min, value.max) > 0;

  const updateRaw = (key: 'min' | 'max', raw: string) => {
    if (!/^\d*$/.test(raw)) return;
    onChange({ ...value, [key]: raw === '' ? null : raw });
  };

  return (
    <div className={`rh-custom-balance-row ${minInvalid || maxInvalid || rangeInvalid ? 'invalid' : ''}`}>
      <div className="rh-custom-balance-head">
        <span className="rh-custom-balance-index">{index + 1}</span>
        <code title={value.contract}>{value.contract}</code>
        <button type="button" aria-label={`Remove ${value.contract}`} onClick={onRemove}><IconTrash size={14} /></button>
      </div>
      <div className="rh-balance-range-inputs">
        <label>
          <span>RAW MIN</span>
          <input
            inputMode="numeric"
            placeholder="No minimum"
            value={value.min ?? ''}
            onChange={(event) => updateRaw('min', event.currentTarget.value)}
          />
        </label>
        <label>
          <span>RAW MAX</span>
          <input
            inputMode="numeric"
            placeholder="No maximum"
            value={value.max ?? ''}
            onChange={(event) => updateRaw('max', event.currentTarget.value)}
          />
        </label>
      </div>
      <small className="rh-custom-balance-hint">
        {minInvalid || maxInvalid
          ? 'Value must fit UInt256.'
          : rangeInvalid
            ? 'Raw minimum cannot exceed raw maximum.'
            : value.min == null && value.max == null
              ? 'No limits: this token will only be loaded into the result.'
              : 'Raw UInt256 strings; no decimal conversion is applied.'}
      </small>
    </div>
  );
}

export default function BalanceFiltersControl({
  filters,
  onChange,
}: {
  filters: RobinhoodFilters;
  onChange: (patch: Partial<BalanceFilters>) => void;
}) {
  const [contract, setContract] = useState('');
  const [contractError, setContractError] = useState<string | null>(null);
  const custom = filters.custom_balance || [];
  const modeOr = filters.balance_mode_or ?? true;
  const hasEthDetails = filters.eth_balance_min != null || filters.eth_balance_max != null
    || filters.weth_balance_min != null || filters.weth_balance_max != null;
  const [ethDetailsOpen, setEthDetailsOpen] = useState(hasEthDetails);

  useEffect(() => {
    if (hasEthDetails) setEthDetailsOpen(true);
  }, [hasEthDetails]);

  const addContract = () => {
    const normalized = contract.trim().toLowerCase();
    if (!CONTRACT_PATTERN.test(normalized)) {
      setContractError('Enter 0x followed by exactly 40 hexadecimal characters.');
      return;
    }
    if (custom.some((item) => item.contract.toLowerCase() === normalized)) {
      setContractError('This contract is already added.');
      return;
    }
    onChange({
      custom_balance: [...custom, { contract: normalized as `0x${string}`, min: null, max: null }],
    });
    setContract('');
    setContractError(null);
  };

  const updateCustom = (index: number, value: CustomBalanceFilter) => {
    onChange({ custom_balance: custom.map((item, itemIndex) => itemIndex === index ? value : item) });
  };

  return (
    <section className="rh-balance-control" aria-labelledby="rh-balance-title">
      <div className="rh-balance-title-row">
        <div>
          <div className="rh-field-label"><IconCoin size={17} /><strong id="rh-balance-title">Wallet balances</strong></div>
          <p>Filter native ETH, WETH, their combined total, USDG, or custom token balances.</p>
        </div>
      </div>

      <div className="rh-balance-mode" role="group" aria-label="Balance matching mode">
        <button type="button" className={modeOr ? 'active' : ''} onClick={() => onChange({ balance_mode_or: true })}>
          <strong>ANY</strong><span>OR · match at least one individual balance</span>
        </button>
        <button type="button" className={!modeOr ? 'active' : ''} onClick={() => onChange({ balance_mode_or: false })}>
          <strong>ALL</strong><span>AND · match every individual balance</span>
        </button>
      </div>
      <p className="rh-balance-group-note">Total ETH is always required with AND. ANY/ALL combines WETH, ETH, USDG and custom tokens.</p>

      <div className="rh-built-in-balances">
        <BuiltInBalanceRange
          token="Total ETH"
          description="Native ETH + WETH · always AND"
          wide
          decimals={BUILT_IN_BALANCE_PRECISION}
          min={filters.total_eth_balance_min ?? null}
          max={filters.total_eth_balance_max ?? null}
          onMinChange={(value) => onChange({ total_eth_balance_min: value })}
          onMaxChange={(value) => onChange({ total_eth_balance_max: value })}
        >
          <button
            type="button"
            className={`rh-balance-details-toggle ${ethDetailsOpen ? 'open' : ''}`}
            aria-expanded={ethDetailsOpen}
            aria-controls="rh-eth-balance-details"
            onClick={() => setEthDetailsOpen((open) => !open)}
          >
            <span><strong>Additional settings</strong><small>Native ETH and WETH limits</small></span>
            {ethDetailsOpen ? <IconChevronUp size={15} /> : <IconChevronDown size={15} />}
          </button>

          {ethDetailsOpen && (
            <div id="rh-eth-balance-details" className="rh-built-in-balances rh-eth-balance-details">
              <BuiltInBalanceRange
                token="ETH"
                description="Native wallet balance"
                decimals={BUILT_IN_BALANCE_PRECISION}
                min={filters.eth_balance_min ?? null}
                max={filters.eth_balance_max ?? null}
                onMinChange={(value) => onChange({ eth_balance_min: value })}
                onMaxChange={(value) => onChange({ eth_balance_max: value })}
              />
              <BuiltInBalanceRange
                token="WETH"
                description="Wrapped ETH token balance"
                decimals={BUILT_IN_BALANCE_PRECISION}
                min={filters.weth_balance_min ?? null}
                max={filters.weth_balance_max ?? null}
                onMinChange={(value) => onChange({ weth_balance_min: value })}
                onMaxChange={(value) => onChange({ weth_balance_max: value })}
              />
            </div>
          )}
        </BuiltInBalanceRange>
        <BuiltInBalanceRange
          token="USDG"
          description="USDG token balance"
          wide
          decimals={BUILT_IN_BALANCE_PRECISION}
          min={filters.usdg_balance_min ?? null}
          max={filters.usdg_balance_max ?? null}
          onMinChange={(value) => onChange({ usdg_balance_min: value })}
          onMaxChange={(value) => onChange({ usdg_balance_max: value })}
        />
      </div>

      <div className="rh-custom-balances">
        <div className="rh-custom-balances-title">
          <div><strong>Custom tokens</strong><span>Contract address + raw UInt256 limits</span></div>
          {custom.length > 0 && <b>{custom.length}</b>}
        </div>
        <div className={`rh-add-contract ${contractError ? 'invalid' : ''}`}>
          <input
            value={contract}
            spellCheck={false}
            autoCapitalize="none"
            placeholder="0x… token contract"
            aria-label="Custom token contract"
            onChange={(event) => { setContract(event.currentTarget.value); setContractError(null); }}
            onKeyDown={(event) => { if (event.key === 'Enter') { event.preventDefault(); addContract(); } }}
          />
          <button type="button" onClick={addContract}><IconPlus size={14} /> Add token</button>
        </div>
        {contractError && <small className="rh-contract-error">{contractError}</small>}

        {custom.length === 0 ? (
          <div className="rh-custom-empty">No custom contracts added.</div>
        ) : (
          <div className="rh-custom-balance-list">
            {custom.map((item, index) => (
              <CustomBalanceRow
                key={item.contract}
                value={item}
                index={index}
                onChange={(value) => updateCustom(index, value)}
                onRemove={() => onChange({ custom_balance: custom.filter((_, itemIndex) => itemIndex !== index) })}
              />
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
